import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  ArrowRight,
  Shield,
  Users,
  Building2,
  Lock,
  Key,
  FileCheck,
  Server,
  Headphones,
  BarChart3,
  Settings,
  Globe,
  Zap,
  CheckCircle,
  ChevronDown,
  Play,
  TrendingUp,
  Clock,
  Code2,
  GitPullRequest,
  Cloud,
  HardDrive,
  Sparkles,
  Check,
  X,
  Star,
  Quote,
  Bot,
  Brain,
  Gem,
  Bolt,
  Activity
} from 'lucide-react';
import { EnterpriseBackground } from '@/components/ui/EnterpriseBackground';

const enterpriseLogos = [
  { name: 'Samsung', size: 'text-xl' },
  { name: 'NVIDIA', size: 'text-2xl font-black' },
  { name: 'Stripe', size: 'text-xl' },
  { name: 'Coinbase', size: 'text-xl' },
  { name: 'Figma', size: 'text-xl' },
  { name: 'OpenAI', size: 'text-xl' },
  { name: 'Rippling', size: 'text-xl' },
  { name: 'Adobe', size: 'text-2xl' },
];

const securityFeaturesData = [
  { key: 'zeroDataRetention', icon: Lock, color: 'from-red-500 to-orange-500' },
  { key: 'sso', icon: Key, color: 'from-purple-500 to-fuchsia-500' },
  { key: 'scim', icon: Users, color: 'from-green-500 to-emerald-500' },
  { key: 'adminControls', icon: Settings, color: 'from-purple-500 to-violet-500' },
  { key: 'soc2', icon: FileCheck, color: 'from-amber-500 to-yellow-500' },
  { key: 'gdpr', icon: Globe, color: 'from-pink-500 to-rose-500' },
  { key: 'encryption', icon: Shield, color: 'from-indigo-500 to-blue-500' },
  { key: 'onPremise', icon: Server, color: 'from-slate-500 to-zinc-600' },
  { key: 'auditLogs', icon: BarChart3, color: 'from-violet-500 to-purple-500' },
];

const testimonialsData = [
  { key: '0', avatar: 'AY', rating: 5 },
  { key: '1', avatar: 'ZK', rating: 5 },
  { key: '2', avatar: 'MD', rating: 5 },
];

const statsData = [
  { value: 64, suffix: '%', labelKey: 'fortune500', icon: Building2 },
  { value: 50, suffix: 'K+', labelKey: 'enterpriseUsers', icon: Users },
  { value: 100, suffix: 'M+', labelKey: 'linesDaily', icon: Code2 },
  { value: 99.9, suffix: '%', labelKey: 'uptimeSla', icon: Activity },
];

const deploymentOptionsData = [
  { key: 'cloudHosted', icon: Cloud, recommended: true, color: 'from-purple-500 to-fuchsia-500' },
  { key: 'onPremise', icon: HardDrive, recommended: false, color: 'from-slate-500 to-zinc-600' },
  { key: 'hybrid', icon: Server, recommended: false, color: 'from-purple-500 to-violet-500' },
];

const comparisonFeaturesData = [
  { key: 'aiCompletion', ochtopus: true, others: true },
  { key: 'unitTesting', ochtopus: true, others: false },
  { key: 'uatTesting', ochtopus: true, others: false },
  { key: 'onPremise', ochtopus: true, others: false },
  { key: 'zeroData', ochtopus: true, others: false },
  { key: 'multiModel', ochtopus: true, others: true },
  { key: 'soc2', ochtopus: true, others: true },
  { key: 'scim', ochtopus: true, others: false },
  { key: 'customAi', ochtopus: true, others: false },
  { key: 'unlimitedSeats', ochtopus: true, others: false },
];

const faqKeys = ['onPremise', 'aiModels', 'billing', 'compliance', 'training'];

function AnimatedStat({ stat, index, t }: { stat: typeof statsData[0]; index: number; t: (key: string) => string }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), index * 200);
    return () => clearTimeout(timer);
  }, [index]);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const increment = stat.value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += 1;
      setCount(Math.min(Math.round(increment * current * 10) / 10, stat.value));
      if (current >= steps) clearInterval(timer);
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, stat.value]);

  return (
    <div className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-accent to-purple-500 flex items-center justify-center shadow-lg shadow-accent/25">
        <stat.icon className="text-white" size={28} />
      </div>
      <div className="text-3xl md:text-4xl font-bold gradient-text mb-2">
        {count}{stat.suffix}
      </div>
      <div className="text-text-secondary text-sm">{t(`enterprise.stats.${stat.labelKey}`)}</div>
    </div>
  );
}

function DashboardPreview({ t }: { t: (key: string) => string }) {
  const [metrics, setMetrics] = useState({
    lines: 0,
    suggestions: 0,
    hours: 0
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(prev => ({
        lines: Math.min(prev.lines + 247, 12450),
        suggestions: Math.min(prev.suggestions + 2, 89),
        hours: Math.min(prev.hours + 1, 48)
      }));
    }, 50);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="rounded-2xl border border-border-default overflow-hidden bg-bg-card shadow-2xl">
      <div className="p-4 border-b border-border-default bg-bg-elevated/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
          </div>
          <span className="text-sm font-medium">{t('enterprise.dashboard.title')}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-tertiary">
          <Activity size={12} className="text-success animate-pulse" />
          {t('enterprise.dashboard.live')}
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* Metric Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-bg-elevated border border-border-default">
            <div className="text-2xl font-bold text-success">+{metrics.lines.toLocaleString()}</div>
            <div className="text-xs text-text-tertiary">{t('enterprise.dashboard.linesToday')}</div>
          </div>
          <div className="p-4 rounded-xl bg-bg-elevated border border-border-default">
            <div className="text-2xl font-bold text-accent">{metrics.suggestions}%</div>
            <div className="text-xs text-text-tertiary">{t('enterprise.dashboard.aiAccepted')}</div>
          </div>
          <div className="p-4 rounded-xl bg-bg-elevated border border-border-default">
            <div className="text-2xl font-bold text-warning">{metrics.hours}h</div>
            <div className="text-xs text-text-tertiary">{t('enterprise.dashboard.timeSaved')}</div>
          </div>
        </div>

        {/* Activity Chart */}
        <div className="h-32 bg-bg-elevated rounded-xl p-4 border border-border-default">
          <div className="flex items-end justify-between h-full gap-2">
            {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88].map((height, i) => (
              <div
                key={i}
                className="flex-1 bg-gradient-to-t from-accent to-purple-500 rounded-t-sm opacity-80 hover:opacity-100 transition-opacity"
                style={{ height: `${height}%`, animationDelay: `${i * 100}ms` }}
              />
            ))}
          </div>
        </div>

        {/* Team Activity */}
        <div className="space-y-3">
          <div className="text-xs text-text-tertiary uppercase tracking-wide">{t('enterprise.dashboard.recentActivity')}</div>
          {[
            { user: 'Alex K.', actionKey: 'mergedPr', time: '2m', color: 'bg-green-500' },
            { user: 'Sarah M.', actionKey: 'aiGenerated', time: '5m', color: 'bg-accent' },
            { user: 'Mike D.', actionKey: 'deployed', time: '12m', color: 'bg-purple-500' },
          ].map((activity, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <div className={`w-2 h-2 rounded-full ${activity.color}`} />
              <span className="font-medium">{activity.user}</span>
              <span className="text-text-tertiary">{t(`enterprise.dashboard.activities.${activity.actionKey}`)}</span>
              <span className="text-text-tertiary text-xs ml-auto">{activity.time} {t('enterprise.dashboard.ago')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function EnterprisePage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);

  return (
    <>
      <SEO {...SEO_CONFIG.enterprise} />
      <div>
      {/* Hero */}
      <section className="section-lg relative overflow-hidden min-h-[700px] flex items-center">
        <EnterpriseBackground />

        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
              <Building2 size={16} />
              <span>{t('enterprise.badge')}</span>
            </div>

            <h1 className="heading-xl mb-6">
              {t('enterprise.title')}{' '}
              <span className="relative">
                <span className="gradient-text">{t('enterprise.titleHighlight')}</span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                  <path d="M2 10C40 4 80 4 100 7C140 10 180 6 198 3" stroke="url(#grad)" strokeWidth="3" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="grad" x1="0" y1="0" x2="200" y2="0">
                      <stop offset="0%" stopColor="#6366f1"/>
                      <stop offset="100%" stopColor="#a855f7"/>
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h1>

            <p className="text-xl text-text-secondary mb-10 max-w-2xl mx-auto">
              {t('enterprise.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Link to="/contact" className="btn btn-primary btn-lg group">
                {t('enterprise.contactSales')}
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <button className="btn btn-secondary btn-lg group">
                <Play size={18} />
                {t('enterprise.watchDemo')}
              </button>
            </div>

            <p className="text-text-tertiary text-sm">
              {t('enterprise.noCreditCard')}
            </p>
          </div>

          {/* Trusted By */}
          <div className="mt-20">
            <p className="text-center text-text-tertiary text-sm mb-8">
              {t('enterprise.trustedBy')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-10 md:gap-14">
              {enterpriseLogos.map((company, i) => (
                <span
                  key={company.name}
                  className={`text-text-tertiary ${company.size} font-semibold opacity-40 hover:opacity-100 transition-all duration-300 hover:text-accent cursor-default`}
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  {company.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {statsData.map((stat, i) => (
              <AnimatedStat key={stat.labelKey} stat={stat} index={i} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits with Live Dashboard */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-success/10 text-success text-sm mb-4">
                <TrendingUp size={14} />
                {t('enterprise.provenResults')}
              </div>
              <h2 className="heading-lg mb-4">
                {t('enterprise.benefits.title')}{' '}
                <span className="gradient-text">{t('enterprise.benefits.titleHighlight')}</span>
              </h2>
              <p className="text-text-secondary mb-8">
                {t('enterprise.benefits.subtitle')}
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  { value: '40%', labelKey: 'fasterReviews', icon: Clock },
                  { value: '25%', labelKey: 'prVelocity', icon: GitPullRequest },
                  { value: '3x', labelKey: 'fasterOnboarding', icon: Users },
                  { value: '60%', labelKey: 'lessContextSwitching', icon: Zap },
                ].map((item) => (
                  <div key={item.labelKey} className="p-4 rounded-xl bg-bg-surface border border-border-default group hover:border-accent/30 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                        <item.icon size={18} className="text-accent" />
                      </div>
                      <span className="text-2xl font-bold gradient-text">{item.value}</span>
                    </div>
                    <div className="text-sm text-text-secondary">{t(`enterprise.benefits.${item.labelKey}`)}</div>
                  </div>
                ))}
              </div>

              <Link to="/contact" className="btn btn-primary">
                {t('enterprise.benefits.getStarted')}
                <ArrowRight size={18} />
              </Link>
            </div>

            <DashboardPreview t={t} />
          </div>
        </div>
      </section>

      {/* Deployment Options */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-muted text-accent text-sm mb-4">
              <Server size={14} />
              {t('enterprise.deployment.badge')}
            </div>
            <h2 className="heading-lg mb-4">
              {t('enterprise.deployment.title')}{' '}
              <span className="gradient-text">{t('enterprise.deployment.titleHighlight')}</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('enterprise.deployment.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {deploymentOptionsData.map((option) => {
              const features = t(`enterprise.deployment.${option.key}.features`, { returnObjects: true }) as string[];
              return (
                <div
                  key={option.key}
                  className={`relative rounded-2xl border bg-bg-card p-6 transition-all duration-300 hover:-translate-y-1 ${
                    option.recommended ? 'border-accent shadow-lg shadow-accent/10' : 'border-border-default hover:border-accent/30'
                  }`}
                >
                  {option.recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-accent text-white text-xs font-medium">
                      {t('enterprise.deployment.recommended')}
                    </div>
                  )}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center mb-4 shadow-lg`}>
                    <option.icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{t(`enterprise.deployment.${option.key}.title`)}</h3>
                  <p className="text-text-secondary text-sm mb-4">{t(`enterprise.deployment.${option.key}.description`)}</p>
                  <ul className="space-y-2">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-text-secondary">
                        <CheckCircle size={14} className="text-success flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('enterprise.comparison.title')}{' '}
              <span className="gradient-text">{t('enterprise.comparison.titleHighlight')}</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('enterprise.comparison.subtitle')}
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="rounded-2xl border border-border-default overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-3 bg-bg-elevated p-4 border-b border-border-default">
                <div className="font-medium">{t('enterprise.comparison.feature')}</div>
                <div className="text-center">
                  <span className="font-semibold gradient-text">Ochtopus</span>
                </div>
                <div className="text-center text-text-tertiary">{t('enterprise.comparison.others')}</div>
              </div>

              {/* Rows */}
              {comparisonFeaturesData.map((item, i) => (
                <div
                  key={item.key}
                  className={`grid grid-cols-3 p-4 ${i !== comparisonFeaturesData.length - 1 ? 'border-b border-border-default' : ''} hover:bg-bg-elevated/50 transition-colors`}
                >
                  <div className="text-text-secondary">{t(`enterprise.comparison.features.${item.key}`)}</div>
                  <div className="flex justify-center">
                    {item.ochtopus ? (
                      <div className="w-6 h-6 rounded-full bg-success/20 flex items-center justify-center">
                        <Check size={14} className="text-success" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-error/20 flex items-center justify-center">
                        <X size={14} className="text-error" />
                      </div>
                    )}
                  </div>
                  <div className="flex justify-center">
                    {item.others ? (
                      <div className="w-6 h-6 rounded-full bg-success/20 flex items-center justify-center">
                        <Check size={14} className="text-success" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-error/20 flex items-center justify-center">
                        <X size={14} className="text-error" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-success/10 text-success text-sm mb-4">
              <Shield size={14} />
              {t('enterprise.security.badge')}
            </div>
            <h2 className="heading-lg mb-4">
              {t('enterprise.security.title')}{' '}
              <span className="gradient-text">{t('enterprise.security.titleHighlight')}</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('enterprise.security.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {securityFeaturesData.map((feature, i) => (
              <div
                key={feature.key}
                className="group relative rounded-xl border border-border-default bg-bg-card p-5 hover:border-accent/30 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                onMouseEnter={() => setHoveredFeature(i)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 blur-2xl transition-opacity duration-500`} />
                <div className={`relative w-10 h-10 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                  <feature.icon className="text-white" size={20} />
                </div>
                <h3 className="font-semibold mb-2 relative">{t(`enterprise.security.${feature.key}.title`)}</h3>
                <p className="text-text-secondary text-sm relative">{t(`enterprise.security.${feature.key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Models */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('enterprise.aiModels.title')}{' '}
              <span className="gradient-text">{t('enterprise.aiModels.titleHighlight')}</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('enterprise.aiModels.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { name: 'GPT-4.5 Turbo', provider: 'OpenAI', icon: Bot, color: 'from-green-500 to-emerald-600' },
              { name: 'Claude Opus 4.5', provider: 'Anthropic', icon: Brain, color: 'from-orange-500 to-amber-600' },
              { name: 'Gemini 2.5 Pro', provider: 'Google', icon: Gem, color: 'from-blue-500 to-indigo-600' },
              { name: 'Grok 3', provider: 'xAI', icon: Bolt, color: 'from-purple-500 to-violet-600' },
            ].map((model) => (
              <div key={model.name} className="text-center p-6 rounded-xl border border-border-default bg-bg-card hover:border-accent/30 transition-all group">
                <div className={`w-14 h-14 mx-auto rounded-xl bg-gradient-to-br ${model.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                  <model.icon className="text-white" size={24} />
                </div>
                <h3 className="font-semibold">{model.name}</h3>
                <p className="text-text-tertiary text-sm">{model.provider}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('enterprise.testimonials.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonialsData.map((testimonial) => (
              <div key={testimonial.key} className="rounded-2xl border border-border-default bg-bg-card p-6 hover:border-accent/30 transition-colors">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-warning fill-warning" />
                  ))}
                </div>
                <Quote size={24} className="text-accent/30 mb-2" />
                <blockquote className="text-lg mb-6">
                  "{t(`enterprise.testimonials.${testimonial.key}.quote`)}"
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent to-purple-500 flex items-center justify-center text-white font-semibold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-medium">{t(`enterprise.testimonials.${testimonial.key}.author`)}</div>
                    <div className="text-text-tertiary text-sm">
                      {t(`enterprise.testimonials.${testimonial.key}.role`)}, {t(`enterprise.testimonials.${testimonial.key}.company`)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-muted text-accent text-sm mb-4">
                <Headphones size={14} />
                {t('enterprise.support.badge')}
              </div>
              <h2 className="heading-lg mb-4">
                {t('enterprise.support.title')}{' '}
                <span className="gradient-text">{t('enterprise.support.titleHighlight')}</span>
              </h2>
              <p className="text-text-secondary mb-8">
                {t('enterprise.support.subtitle')}
              </p>
              <ul className="space-y-4">
                {(t('enterprise.support.features', { returnObjects: true }) as string[]).map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-text-secondary">
                    <div className="w-6 h-6 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={14} className="text-success" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-gradient-to-br from-accent to-purple-600 p-8 text-white text-center">
              <Building2 className="mx-auto mb-4 opacity-80" size={48} />
              <h3 className="text-2xl font-bold mb-4">{t('enterprise.support.ctaTitle')}</h3>
              <p className="opacity-80 mb-6">
                {t('enterprise.support.ctaSubtitle')}
              </p>
              <Link to="/contact" className="btn bg-white text-accent hover:bg-white/90 btn-lg w-full">
                {t('enterprise.contactSales')}
                <ArrowRight size={20} />
              </Link>
              <p className="text-sm opacity-60 mt-4">
                {t('enterprise.support.responseTime')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-bg-surface">
        <div className="container max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('enterprise.faq.title')}
            </h2>
          </div>

          <div className="space-y-4">
            {faqKeys.map((key, index) => (
              <div
                key={key}
                className="rounded-xl border border-border-default bg-bg-card overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-bg-elevated/50 transition-colors"
                >
                  <h3 className="font-semibold pr-4">{t(`enterprise.faq.${key}.q`)}</h3>
                  <ChevronDown
                    size={20}
                    className={`text-text-tertiary flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-48' : 'max-h-0'
                  }`}
                >
                  <p className="px-6 pb-6 text-text-secondary">{t(`enterprise.faq.${key}.a`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact" className="btn btn-primary btn-lg">
              {t('enterprise.faq.moreQuestions')}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
